/**
 * Panneau "heure explorée" — affiché entre le strip 7j et le graphique.
 *
 * Affiche les 4 variables (T° / pluie / vent / humidité) de l'heure pointée
 * par le curseur. En mode "maintenant", un dot pulsé signale l'heure courante ;
 * en mode "exploration", un bouton ↺ à gauche permet de revenir à maintenant.
 *
 * Le slot bouton est à largeur fixe (w-7) et masqué via visibility en mode
 * "maintenant" → l'icône + date + valeurs ne bougent jamais d'un mode à l'autre.
 */

import { formatHeureLongue, getWeatherMeta } from "./formatters.js";
import type { MeteoPoint } from "./types.js";

export function PanneauHeureExploree({
  point,
  estMaintenant,
  onResetMaintenant,
}: {
  point: MeteoPoint;
  estMaintenant: boolean;
  onResetMaintenant: () => void;
}) {
  const meta = getWeatherMeta(point.weather_code);
  const Icone = meta.icon;
  return (
    <div
      className={`flex flex-wrap items-center justify-between gap-3 rounded-md border px-3 py-2.5 transition-colors ${
        estMaintenant ? "border-champagne-300 bg-champagne-50" : "border-champagne-200/70 bg-white"
      }`}
    >
      <div className="flex items-center gap-2 leading-tight">
        {/* Slot bouton retour (largeur fixe) — l'espace est réservé en
            permanence pour stabiliser le layout. */}
        <div className="w-7 shrink-0">
          {!estMaintenant ? (
            <button
              type="button"
              onClick={onResetMaintenant}
              title="Revenir sur l'heure actuelle"
              aria-label="Revenir sur l'heure actuelle"
              className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-champagne-100 text-[14px] text-champagne-800 hover:bg-champagne-200 focus:outline-none focus:ring-2 focus:ring-champagne-300"
            >
              ↺
            </button>
          ) : null}
        </div>
        <Icone aria-hidden className="h-5 w-5 shrink-0" stroke={meta.color} strokeWidth={1.6} />
        <div>
          <div className="flex items-center gap-2 text-[13px] font-medium text-champagne-900">
            {estMaintenant ? (
              <span
                aria-hidden
                className="inline-block h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-vine-500"
                title="Heure actuelle"
              />
            ) : null}
            <span>{formatHeureLongue(point.time)}</span>
          </div>
          <div className="text-[11px] text-champagne-700">{meta.label}</div>
        </div>
      </div>
      <div className="mono tnum flex flex-wrap items-baseline gap-x-4 gap-y-1 text-[12px]">
        <span>
          <strong className="text-[15px] font-semibold text-champagne-900">
            {Math.round(point.temperature_c)}°C
          </strong>
        </span>
        <span className="text-champagne-700">
          pluie{" "}
          <strong className="text-champagne-900">{point.precipitation_mm.toFixed(1)} mm</strong>
        </span>
        <span className="text-champagne-700">
          vent <strong className="text-champagne-900">{Math.round(point.wind_kmh)} km/h</strong>
        </span>
        <span className="text-champagne-700">
          humidité <strong className="text-champagne-900">{Math.round(point.humidity_pct)}%</strong>
        </span>
      </div>
    </div>
  );
}
